import {
  JSX, useEffect, useState,
} from 'react';
import { Box } from '@mui/material';
import { IClaritySubmission, IPatient, type IBiomaterial } from '@zero-dash/types';
import CustomTypography from '@/components/Common/Typography';
import LoadingAnimation from '@/components/Animations/LoadingAnimation';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import ClaritySubmissionBlock from './ClaritySubmissionBlock';
import { ScrollableSection } from '@/components/ScrollableSection/ScrollableSection';

interface IProps {
  patientRecords: IPatient[];
  biomaterials: IBiomaterial[];
}

export default function ClaritySubmissionsPanel({
  patientRecords,
  biomaterials,
}: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const [submissions, setSubmissions] = useState<IClaritySubmission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchSubmissions = async (): Promise<void> => {
      if (!patientRecords.length) return;

      setLoading(true);
      try {
        const dedupPairs: [string, string][] = [...new Map(
          patientRecords.map((p) => {
            const pair: [string, string] = [p.patientId, p.lmSubjectId.toString()];
            return [`${p.patientId}::${p.lmSubjectId}`, pair];
          }),
        ).values()];

        const results = await Promise.all(
          dedupPairs.map(([patientId, lmSubjectId]) => (
            zeroDashSdk.patient.getClaritySubmissions(patientId, lmSubjectId)
          )),
        );

        setSubmissions(results.flat().sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ));
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [patientRecords, zeroDashSdk.patient]);

  if (loading) {
    return <LoadingAnimation />;
  }

  if (!submissions.length) {
    return (
      <Box display="flex" justifyContent="center" py="32px">
        <CustomTypography variant="bodyRegular">No previous submissions found.</CustomTypography>
      </Box>
    );
  }

  return (
    <ScrollableSection style={{ flex: 1, minHeight: 0 }}>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {submissions.map((submission) => (
          <ClaritySubmissionBlock
            key={submission.id}
            submission={submission}
            biomaterials={biomaterials}
          />
        ))}
      </Box>
    </ScrollableSection>
  );
}
